import { getAuthCookie } from "../utils/authCookies";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://dev.realesy.com/api/v1";

export interface PostTypeTemplateAsset {
  key: string;
  content_type?: string;
  file_ext?: string;
}

export interface PostTypeTemplate {
  id: string;
  schema?: {
    illustration?: {
      name?: string;
      viewBox?: string;
      slots?: Array<{
        key: string;
        label?: string;
        rect_norm?: { x: number; y: number; w: number; h: number };
        ui?: { label_y?: number; label_x?: number; font_size?: number };
      }>;
      assets?: {
        thumbnail?: PostTypeTemplateAsset;
        svg?: PostTypeTemplateAsset;
        preview_png?: PostTypeTemplateAsset;
        [key: string]: PostTypeTemplateAsset | undefined;
      };
    };
    defaults?: {
      viewBox?: string;
      viewBox_w?: number;
      viewBox_h?: number;
    };
  };
  [key: string]: unknown;
}

export interface PostTypeTemplatesResponse {
  items: PostTypeTemplate[];
  total: number;
  limit: number;
  page: number;
}

interface FetchTemplatesOptions {
  token: string;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}

export async function getAllPostTypeTemplates({
  token,
  page = 1,
  limit = 50,
  signal,
}: FetchTemplatesOptions): Promise<PostTypeTemplatesResponse> {
  const url = new URL(`${API_BASE_URL}/post-type-templates`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to fetch post type templates (${response.status})`);
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    limit: data.limit ?? limit,
    page: data.page ?? page,
  };
}

/** GET post-type-templates/{templateId} — fetch single template by illustration ID (frame/schema + assets) */
export async function getPostTypeTemplateById(
  templateId: string,
  token: string,
  signal?: AbortSignal
): Promise<PostTypeTemplate> {
  const url = `${API_BASE_URL}/post-type-templates/${encodeURIComponent(templateId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to fetch post type template (${response.status})`);
  }

  const data = await response.json();
  return {
    id: data.id ?? data._id ?? templateId,
    schema: data.schema,
    ...data,
  };
}

export interface PresignedFile {
  id: string;
  name?: string;
  mime_type?: string;
  size?: number;
  parent_id?: string;
  presigned_url: string;
}

interface PresignOptions {
  token: string;
  fileIds: string[];
  signal?: AbortSignal;
}

export async function getPresignedAssetUrls({
  token,
  fileIds,
  signal,
}: PresignOptions): Promise<Record<string, PresignedFile>> {
  if (!fileIds.length) {
    return {};
  }

  const response = await fetch(`${API_BASE_URL}/work-drive/files/presign`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ file_ids: fileIds }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to fetch presigned file URLs (${response.status})`);
  }

  const data = (await response.json()) as PresignedFile[];
  return data.reduce<Record<string, PresignedFile>>((acc, file) => {
    acc[file.id] = file;
    return acc;
  }, {});
}

export interface PostTypeSlotEntity {
  id?: string;
  _id?: string;
  key: string;
  key_name?: string;
  label?: string;
  required?: boolean;
  sort?: number;
  zone?: { x: number; y: number; w: number; h: number };
  rect_norm?: { x: number; y: number; w: number; h: number };
  input_type?: string;
  status?: boolean;
  /** "always" = always visible; "toggle" = user can add/remove (e.g. riders) */
  display_mode?: "always" | "toggle";
  /** For toggle slots: show expanded by default when true */
  default_visible?: boolean;
  /** If true, show delete icon to remove/hide slot */
  allow_remove?: boolean;
  /** "panel" | "rider" etc. */
  group?: string;
  brands?: Array<{ id: string; name?: string; logo?: string | null }>;
  categories?: Array<{ id: string; name?: string; image?: string | null }>;
  products?: Array<{ id: string; name?: string; sku?: string | null }>;
  [key: string]: unknown;
}

/** Group / brokerage row on `post_type` (POST Type Assignments) — matches backend shape. */
export interface PostTypeGroupBrokerageRow {
  id: string;
  name: string;
  image?: string;
  select?: boolean;
}

export interface PostTypePayload {
  name: string;
  slug: string;
  description?: string;
  illustration?: string;
  post_image_id?: string;
  available_for_installation?: boolean;
  is_default_post_type?: boolean;
  status?: boolean;
  slots: PostTypeSlotEntity[];
  groups?: PostTypeGroupBrokerageRow[];
  brokerage_firms?: PostTypeGroupBrokerageRow[];
  /** Offices under selected brokerages (same row shape as groups when backend supports it). */
  offices?: PostTypeGroupBrokerageRow[];
  select_all_groups?: boolean;
  select_all_brokerage?: boolean;
}

/** Accessory rule selections shape for create post type (same as slot selections). */
export interface CreatePostTypeAccessoryRule {
  status: boolean;
  select_all_brands?: boolean;
  brands: Array<{
    brand: { id: string; name: string; logo?: string; select?: boolean };
    select_all_categories?: boolean;
    categories: Array<{
      category: { id: string; name: string; image?: string; select?: boolean };
      select_all_products?: boolean;
      products: Array<{ id: string; name: string; sku?: string; select?: boolean }>;
    }>;
  }>;
  categories?: unknown[];
  products?: unknown[];
  groups?: Array<{ groups: { id: string; name: string; image?: string; select?: boolean } }>;
  select_all_groups?: boolean;
  select_all_brokerage?: boolean;
  brokerages?: Array<{ brokerages: { id: string; name: string; image?: string; select?: boolean } }>;
}

/** Request body for POST /vendors/:vendorId/post-types (create with optional accessory rule). */
export interface CreatePostTypeRequestBody {
  post_type: PostTypePayload;
  accessory_rule?: CreatePostTypeAccessoryRule;
}

export interface PostTypeRecord {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  illustration?: string;
  available_for_installation?: boolean;
  is_default_post_type?: boolean;
  status?: boolean;
  slots?: PostTypeSlotEntity[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface PostTypeSlotsResponse {
  post_type_id: string;
  slots: PostTypeSlotEntity[];
}

export interface MarketplaceBrand {
  _id?: string;
  id?: string;
  brand_id?: string;
  brand_name?: string;
  name?: string;
  category_count?: number;
  product_count?: number;
  logo?: string;
}

export interface BrandSelect2Item {
  id: string;
  text: string;
  name: string;
  image?: string;
  product_count?: number;
  [key: string]: unknown;
}

export interface BrandsByUserSelect2Response {
  results: BrandSelect2Item[];
  total: number;
  pagination: { more: boolean };
}

export interface MarketplaceCategory {
  _id?: string;
  id?: string;
  category?: string;
  category_id?: string;
  name?: string;
  image?: string;
  status?: boolean;
  product_count?: number;
  [key: string]: unknown;
}

export interface CategorySelect2Item {
  _id: string;
  id?: string;
  text: string;
  name: string;
  image?: string;
  product_count?: number;
  [key: string]: unknown;
}

export interface CategoriesByUserSelect2Response {
  results: CategorySelect2Item[];
  total: number;
  pagination: { more: boolean };
}

export interface CustomerGroupSelect2Item {
  _id: string;
  id?: string;
  text: string;
  name: string;
  purpose?: string;
  status?: string;
  [key: string]: unknown;
}

export interface CustomerGroupsSelect2Response {
  results: CustomerGroupSelect2Item[];
  total: number;
  pagination: { more: boolean };
}

export interface MarketplaceProductImage {
  url?: string;
  default?: boolean;
}

export interface MarketplaceProduct {
  product_id?: string;
  product_name?: string;
  brand?: string;
  brand_id?: string;
  category?: string;
  category_id?: string;
  sku?: string;
  available?: number;
  price?: number;
  images?: MarketplaceProductImage[];
  description?: string;
  [key: string]: unknown;
}

export interface MarketplaceProductsResponse {
  data: MarketplaceProduct[];
  limit: number;
  page: number;
  total: number;
  totalPages?: number;
}

interface MarketplaceProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Record<string, unknown>;
  sort?: { field: string; order: "asc" | "desc" };
}

/** Fetch a single page of brands by user (Select2 format). Use for AsyncPaginate/Select2 loaders. */
export async function fetchBrandsByUserSelect2(
  userId: string,
  token: string,
  params: { q?: string; page?: number; limit?: number } = {},
  signal?: AbortSignal
): Promise<BrandsByUserSelect2Response> {
  const { q = "", page = 1, limit = 10 } = params;
  const url = new URL(
    `${API_BASE_URL}/brands/by-user/${encodeURIComponent(userId)}/select2`
  );
  url.searchParams.set("q", q);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      errorText || `Failed to fetch brands by user (${response.status})`
    );
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const total =
    typeof payload?.total_brands === "number"
      ? payload.total_brands
      : typeof payload?.total === "number"
        ? payload.total
        : typeof data?.total === "number"
          ? data.total
          : 0;
  return {
    results,
    total,
    pagination: payload?.pagination ?? data?.pagination ?? { more: false },
  };
}

/** Fetch a single page of categories by user (Select2 format). Response wraps in data. */
export async function fetchCategoriesByUserSelect2(
  userId: string,
  token: string,
  params: { q?: string; page?: number; limit?: number } = {},
  signal?: AbortSignal
): Promise<CategoriesByUserSelect2Response> {
  const { q = "", page = 1, limit = 10 } = params;
  const url = new URL(
    `${API_BASE_URL}/categories/by-user/${encodeURIComponent(userId)}/select2`
  );
  url.searchParams.set("q", q);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      errorText || `Failed to fetch categories by user (${response.status})`
    );
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const total =
    typeof payload?.total_categories === "number"
      ? payload.total_categories
      : typeof payload?.total === "number"
        ? payload.total
        : typeof data?.total === "number"
          ? data.total
          : 0;
  return {
    results,
    total,
    pagination: payload?.pagination ?? data?.pagination ?? { more: false },
  };
}

export interface PostTypeAccessoriesCategoriesSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/accessories/categories/select2 — accessory Browse Catalog categories (no slot). */
export async function getPostTypeAccessoriesCategoriesSelect2(
  vendorId: string,
  postTypeId: string,
  token: string,
  params: PostTypeAccessoriesCategoriesSelect2Params = {},
  signal?: AbortSignal
): Promise<{ pagination: { more: boolean }; results: Array<{ id: string; text: string; count?: number; image?: string; name?: string; [key: string]: unknown }> }> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/accessories/categories/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory categories select2 (${response.status})`);
  }
  const json = await response.json();
  return {
    pagination: json?.pagination ?? { more: false },
    results: Array.isArray(json?.results)
      ? json.results
      : Array.isArray(json?.data)
        ? json.data
        : [],
  };
}

export interface BrokerageSelect2Item {
  id: string;
  text: string;
  name?: string;
  office_count?: number;
  [key: string]: unknown;
}

export interface BrokeragesSelect2Response {
  results: BrokerageSelect2Item[];
  total?: number;
  pagination: { more: boolean };
}

export interface OfficeSelect2Item {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface OfficesByBrokerageResponse {
  results: OfficeSelect2Item[];
  total?: number;
  pagination: { more: boolean };
}

/** Brokerages Select2 (created and linked). */
export async function fetchBrokeragesSelect2(
  token: string,
  params?: { q?: string; page?: number; limit?: number },
  signal?: AbortSignal
): Promise<BrokeragesSelect2Response> {
  const { q = "", page = 1, limit = 10 } = params ?? {};
  const url = new URL(`${API_BASE_URL}/created_and_linked_brokerage_firms/select2`);
  url.searchParams.set("q", String(q));
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to fetch brokerages (${response.status})`);
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  const total =
    typeof payload?.total === "number"
      ? payload.total
      : typeof data?.total_brokerages === "number"
        ? data.total_brokerages
        : (payload?.results?.length ?? 0);
  return {
    results: Array.isArray(payload?.results) ? payload.results : [],
    total,
    pagination: payload?.pagination ?? { more: false },
  };
}

/** Offices under a brokerage (POST). */
export async function fetchOfficesByBrokerage(
  brokerageId: string,
  token: string,
  params?: { page?: number; limit?: number; search?: string },
  signal?: AbortSignal
): Promise<OfficesByBrokerageResponse> {
  const { page = 1, limit = 20, search = "" } = params ?? {};
  const response = await fetch(
    `${API_BASE_URL}/office/select/${encodeURIComponent(brokerageId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ page, limit, search }),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to fetch offices (${response.status})`);
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  return {
    results: Array.isArray(payload?.results) ? payload.results : [],
    total: typeof payload?.total === "number" ? payload.total : (payload?.results?.length ?? 0),
    pagination: payload?.pagination ?? { more: false },
  };
}

/** Agents by offices Select2 – POST /agents/offices/select2 */
export async function fetchAgentsByOfficesSelect2(
  token: string,
  officeIds: string[],
  signal?: AbortSignal
): Promise<{ id: string; text: string }[]> {
  const response = await fetch(`${API_BASE_URL}/agents/offices/select2`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ office_ids: officeIds }),
    signal,
  });

  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch agents by offices (${response.status})`);
  }

  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  return Array.isArray(arr) ? arr : [];
}

/** Customer groups Select2 (logged-in user). */
export async function fetchCustomerGroupsSelect2(
  token: string,
  params?: { q?: string; page?: number; limit?: number },
  signal?: AbortSignal
): Promise<CustomerGroupsSelect2Response> {
  const { q = "", page = 1, limit = 10 } = params ?? {};
  const base = getBaseUrl();
  const url = new URL(`${base}/customer-groups/select2`);
  url.searchParams.set("q", String(q));
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      errorText || `Failed to fetch customer groups (${response.status})`
    );
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  return {
    results: Array.isArray(payload?.results) ? payload.results : [],
    total: typeof payload?.total === "number" ? payload.total : 0,
    pagination: payload?.pagination ?? { more: false },
  };
}

/** Fetch all brands by user for grid/ConfigurePanelModal. Uses Select2 endpoint with pagination. */
export async function fetchBrandsByUser(
  userId: string,
  token: string,
  signal?: AbortSignal
): Promise<MarketplaceBrand[]> {
  const all: BrandSelect2Item[] = [];
  let page = 1;
  const limit = 100;
  let hasMore = true;

  while (hasMore) {
    const resp = await fetchBrandsByUserSelect2(
      userId,
      token,
      { q: "", page, limit },
      signal
    );
    all.push(...resp.results);
    hasMore = resp.pagination?.more ?? false;
    if (resp.results.length < limit) break;
    page++;
  }

  return all.map((b) => ({
    id: b.id,
    _id: b.id,
    brand_id: b.id,
    brand_name: b.name ?? b.text,
    name: b.name ?? b.text,
    logo: b.image,
    product_count: b.product_count,
  }));
}

/** @deprecated Use fetchBrandsByUser(userId, token, signal) instead. Brands API now uses /brands/by-user/{userId}/select2 */
export async function fetchSignageMarketplaceBrands(
  _signageId: string,
  token: string,
  signal?: AbortSignal
): Promise<MarketplaceBrand[]> {
  const userId =
    getAuthCookie("userId");
  if (!userId) return [];
  return fetchBrandsByUser(userId, token, signal);
}

export async function fetchCategoriesCreatedBy(
  userId: string,
  token: string,
  signal?: AbortSignal
): Promise<MarketplaceCategory[]> {
  const response = await fetch(
    `${API_BASE_URL}/categories/created-by/${userId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      errorText || `Failed to fetch categories (${response.status})`
    );
  }

  const data = await response.json();
  if (!data || !Array.isArray(data.data)) {
    return [];
  }
  return data.data as MarketplaceCategory[];
}

export async function fetchSignageMarketplaceProducts(
  signageId: string,
  token: string,
  params: MarketplaceProductsParams,
  signal?: AbortSignal
): Promise<MarketplaceProductsResponse> {
  const body = {
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    search: params.search ?? "",
    filters: params.filters ?? {},
    sort: params.sort ?? { field: "created_at", order: "desc" as const },
  };

  const response = await fetch(
    `${API_BASE_URL}/signage/${signageId}/marketplace/products/datatable`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      errorText || `Failed to fetch marketplace products (${response.status})`
    );
  }

  const data = await response.json();
  return {
    data: Array.isArray(data?.data) ? (data.data as MarketplaceProduct[]) : [],
    limit: data?.limit ?? body.limit,
    page: data?.page ?? body.page,
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? undefined,
  };
}

/** Marketplace brands Select2 – GET /signage/{id}/post-types/brands/select2 (?search, ?page, ?limit) */
export interface FetchMarketplaceBrandsSelect2Params {
  search?: string;
  page?: number;
  limit?: number;
}

export interface FetchMarketplaceBrandsSelect2Result {
  data: { id: string; text: string | null; product_count?: number }[];
  total_brands?: number;
  hasMore?: boolean;
}

export async function fetchMarketplaceBrandsSelect2(
  signageId: string,
  token: string,
  params?: FetchMarketplaceBrandsSelect2Params,
  signal?: AbortSignal
): Promise<FetchMarketplaceBrandsSelect2Result> {
  const url = new URL(
    `${API_BASE_URL}/signage/${encodeURIComponent(signageId)}/post-types/brands/select2`
  );
  const q = params?.search?.trim();
  if (q) url.searchParams.set("search", q);
  if (typeof params?.page === "number") url.searchParams.set("page", String(params.page));
  if (typeof params?.limit === "number") url.searchParams.set("limit", String(params.limit));
  const response = await fetch(String(url), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch marketplace brands (${response.status})`);
  }
  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  const data = Array.isArray(arr) ? arr : [];
  const total_brands =
    typeof json?.total_brands === "number" ? json.total_brands : undefined;
  const pagination = json?.pagination;
  const limit = params?.limit ?? 30;
  const hasMore = Boolean(
    pagination?.more ?? (typeof pagination?.has_more === "boolean" ? pagination.has_more : data.length >= limit)
  );
  return { data, total_brands, hasMore };
}

export interface FetchMarketplaceCategoriesByBrandParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface FetchMarketplaceCategoriesByBrandResult {
  data: { id: string; text: string; count?: number }[];
  hasMore: boolean;
  total_categories?: number;
}

/** Vendor brand categories Select2 – GET /vendors/{vendorId}/brands/{brandId}/categories/select2?page=1&limit=10. Response: { data: [ { count, id, text } ] } */
export async function fetchVendorBrandCategoriesSelect2(
  vendorId: string,
  brandId: string,
  token: string,
  params?: FetchMarketplaceCategoriesByBrandParams,
  signal?: AbortSignal
): Promise<FetchMarketplaceCategoriesByBrandResult> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;
  const url = new URL(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/post-types/brands/${encodeURIComponent(brandId)}/categories/select2`
  );
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  const catSearch = params?.search?.trim();
  if (catSearch) url.searchParams.set("search", catSearch);
  const response = await fetch(String(url), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch vendor brand categories (${response.status})`);
  }
  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  const data = Array.isArray(arr) ? arr : [];
  const pagination = json?.pagination;
  const hasMore = Boolean(pagination?.more ?? (data.length >= limit));
  const total_categories =
    typeof json?.total_categories === "number" ? json.total_categories : undefined;
  return { data, hasMore, total_categories };
}

export interface MarketplaceProductSelect2Params {
  brand_id?: string;
  category_id?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort?: { field: string; order: "ASC" | "DESC" | "asc" | "desc" };
}

export interface VendorMarketplaceProductsSelect2Response {
  data: { id: string; text: string; price?: number; sku?: string; image_url?: string; imageUrl?: string; name?: string }[];
  hasMore: boolean;
  total?: number;
}

/** Marketplace products Select2 – POST /signage/{id}/marketplace/products/select2 */
export async function fetchMarketplaceProductsSelect2(
  signageId: string,
  token: string,
  params: MarketplaceProductSelect2Params,
  signal?: AbortSignal
): Promise<{ id: string; text: string; price?: number }[]> {
  const body = {
    filters: {
      brand_id: params.brand_id || undefined,
      category_id: params.category_id || undefined,
    },
    search: params.search ?? "",
    page: params.page ?? 1,
    limit: params.limit ?? 50,
    sort: params.sort ?? { field: "product_name", order: "ASC" as const },
  };
  const response = await fetch(
    `${API_BASE_URL}/signage/${signageId}/post-types/products/select2`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch marketplace products (${response.status})`);
  }
  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  return Array.isArray(arr) ? arr : [];
}

/** Vendor post-type products Select2 – POST /vendors/{vendorId}/post-types/products-select2 (JSON: page, limit, search, filters.brand_id, filters.category_id, sort) */
export async function fetchVendorMarketplaceProductsSelect2(
  vendorId: string,
  token: string,
  params: MarketplaceProductSelect2Params,
  signal?: AbortSignal
): Promise<VendorMarketplaceProductsSelect2Response> {
  const rawSort = params.sort ?? { field: "name", order: "asc" as const };
  const orderStr = String(rawSort.order).toLowerCase();
  const order = orderStr === "desc" ? "desc" : "asc";
  const body = {
    filters: {
      brand_id: params.brand_id || undefined,
      category_id: params.category_id || undefined,
    },
    search: (params.search ?? "").trim(),
    page: params.page ?? 1,
    limit: params.limit ?? 50,
    sort: { field: rawSort.field || "name", order },
  };
  const response = await fetch(
    `${API_BASE_URL}/signage/${encodeURIComponent(vendorId)}/post-types/products/select2`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch vendor marketplace products (${response.status})`);
  }
  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  const data = Array.isArray(arr) ? arr : [];
  const pagination = json?.pagination;
  const limit = params.limit ?? 50;
  const hasMore = Boolean(
    pagination?.more ??
      (typeof pagination?.has_more === "boolean" ? pagination.has_more : data.length >= limit)
  );
  const total = typeof json?.total === "number" ? json.total : undefined;
  return { data, hasMore, total };
}

/** Marketplace profiles Select2 – GET /marketplace-profiles/select2 (filter by category e.g. "signage") */
export interface MarketplaceProfileSelect2Params {
  search?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface MarketplaceProfileSelect2Item {
  id: string;
  text: string;
  vendor_id?: string;
  category?: string;
  tag_line?: string;
  email?: string;
  contact_info?: { email?: string };
}

export interface MarketplaceProfilesSelect2Response {
  results: MarketplaceProfileSelect2Item[];
  pagination: { more: boolean };
}

export async function fetchMarketplaceProfilesSelect2(
  token: string,
  params?: MarketplaceProfileSelect2Params,
  signal?: AbortSignal
): Promise<MarketplaceProfilesSelect2Response> {
  const { search = "", category = "signage", page = 1, limit = 10 } = params ?? {};
  const url = new URL(`${API_BASE_URL}/marketplace-profiles/select2`);
  url.searchParams.set("search", String(search));
  url.searchParams.set("category", String(category));
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch marketplace profiles (${response.status})`);
  }

  const data = await response.json();
  const payload = data?.data ?? data;
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const pagination = payload?.pagination ?? { more: false };
  return { results, pagination };
}

const getBaseUrl = () => process.env.REACT_APP_API_URL || "";

export interface PostTypeDatatableParams {
  page: number;
  limit: number;
  search?: string;
  sort?: { field: string; order: "asc" | "desc" };
  installation_only?: boolean;
}

export interface PostTypeDatatableResponse {
  results: PostTypeRecord[];
  total: number;
}

export async function postTypesDatatable(
  vendorId: string,
  params: PostTypeDatatableParams,
  token: string
): Promise<PostTypeDatatableResponse> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search ?? "",
      sort: params.sort ?? { field: "name", order: "asc" },
      installation_only: params.installation_only ?? false,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Post types datatable failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    results: data.results ?? [],
    total: data.total ?? 0,
  };
}



export async function createPostType(
  vendorId: string,
  body: CreatePostTypeRequestBody | PostTypePayload,
  token: string
): Promise<PostTypeRecord> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types`;
  const isFullBody =
    body != null &&
    typeof body === "object" &&
    "post_type" in body &&
    (body as CreatePostTypeRequestBody).post_type != null;
  const requestBody: CreatePostTypeRequestBody = isFullBody
    ? (body as CreatePostTypeRequestBody)
    : { post_type: body as PostTypePayload };
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to create post type (${response.status})`);
  }

  return response.json();
}

export async function updatePostType(
  vendorId: string,
  postTypeId: string,
  body: CreatePostTypeRequestBody | PostTypePayload,
  token: string
): Promise<PostTypeRecord> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}`;
  const isFullBody =
    body != null &&
    typeof body === "object" &&
    "post_type" in body &&
    (body as CreatePostTypeRequestBody).post_type != null;
  const requestBody: CreatePostTypeRequestBody = isFullBody
    ? (body as CreatePostTypeRequestBody)
    : { post_type: body as PostTypePayload };
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to update post type (${response.status})`);
  }

  return response.json();
}

export async function getPostTypeById(
  vendorId: string,
  postTypeId: string,
  token: string
): Promise<PostTypeRecord> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch post type (${response.status})`);
  }

  return response.json();
}

/** Response from GET /vendors/{vendorId}/post-type-slots/{slotId} — catalog counts for Browse Catalog */
export interface PostTypeSlotCatalogResponse {
  key: string;
  label: string;
  post_type_id: string;
  slot_id: string;
  total_brands: number;
  total_categories: number;
  total_products: number;
}

export async function getPostTypeSlotCatalog(
  vendorId: string,
  slotId: string,
  token: string
): Promise<PostTypeSlotCatalogResponse> {
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-type-slots/${encodeURIComponent(slotId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot catalog (${response.status})`);
  }

  return response.json();
}

/** Slot product row from POST .../post-type-slots/{slotId}/products/datatable */
export interface SlotProductDatatableRow {
  _id: string;
  product: {
    brand?: string;
    brand_id?: string;
    category?: string;
    category_id?: string;
    is_taxable?: boolean;
    production_status?: string;
    shipping_config?: boolean;
    variants?: Array<{
      variant_id?: string;
      variant_name?: string;
      sku?: string;
      attributes?: Record<string, string>;
      images?: Array<{ url: string; default?: boolean; alt_text?: string }>;
      inventory?: Array<{
        available?: number;
        on_hand?: number;
        warehouse_id?: string;
        warehouse_name?: string;
        zone_id?: string;
        zone_name?: string;
        rack_id?: string;
        rack_name?: string;
        bin_id?: string;
        bin_name?: string;
      }>;
      customizations?: { fields?: Array<{ field_name?: string; field_type?: string; required?: boolean }> };
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
}

export interface SlotProductsDatatableParams {
  page?: number;
  limit?: number;
  search?: string;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
}

export interface SlotProductsDatatableResponse {
  results: SlotProductDatatableRow[];
  total: number;
}

export async function getPostTypeSlotProductsDatatable(
  vendorId: string,
  slotId: string,
  token: string,
  params: SlotProductsDatatableParams = {},
  signal?: AbortSignal
): Promise<SlotProductsDatatableResponse> {
  const { page = 1, limit = 10, search = "", fields, sort = { field: "created_at", order: "desc" } } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-type-slots/${encodeURIComponent(slotId)}/products/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields: fields ?? ["_id", "product.brand", "product.brand_id", "product.category", "product.category_id", "product.is_taxable", "product.shipping_config", "product.production_status", "product.variants"],
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot products (${response.status})`);
  }

  const data = await response.json();
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}

/** Accessory products datatable: POST /vendors/{vendorId}/post-types/{postTypeId}/products/datatable (Browse Catalog → Products when Add Accessory, no slot). Body: page, limit, search, fields, sort — same shape as slot products datatable. */
export async function getPostTypeAccessoryProductsDatatable(
  vendorId: string,
  postTypeId: string,
  token: string,
  params: SlotProductsDatatableParams = {},
  signal?: AbortSignal
): Promise<SlotProductsDatatableResponse> {
  const { page = 1, limit = 10, search = "", fields, sort = { field: "created_at", order: "desc" } } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/products/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields: fields ?? ["_id", "product.brand", "product.brand_id", "product.category", "product.category_id", "product.is_taxable", "product.shipping_config", "product.production_status", "product.variants"],
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory products (${response.status})`);
  }

  const data = await response.json();
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}

/** Raw row from POST .../vendors/{vendorId}/post-types/{postTypeId}/accessories/brands/{brandId}/categories/{categoryId}/products */
export interface AccessoryBrandCategoryProductsRawRow {
  _id: string;
  product_id?: string;
  "product.name"?: string;
  "product.sku"?: string;
  "product.brand"?: string;
  "product.brand_id"?: string;
  "product.category"?: string;
  "product.category_id"?: string;
  "product.variants"?: SlotProductDatatableRow["product"]["variants"];
  [key: string]: unknown;
}

export interface AccessoryBrandCategoryProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  category_id: string;
  brand_id: string;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
}

export interface AccessoryBrandCategoryProductsResponse {
  results: AccessoryBrandCategoryProductsRawRow[];
  total?: number;
  pagination?: { more?: boolean };
}

export interface AccessoryCategoryProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  category_id: string;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
}

/** Accessory products by brand+category (Add Accessory > Brand > Category). */
export async function getPostTypeAccessoryBrandCategoryProducts(
  vendorId: string,
  postTypeId: string,
  token: string,
  params: AccessoryBrandCategoryProductsParams,
  signal?: AbortSignal
): Promise<AccessoryBrandCategoryProductsResponse> {
  const {
    page = 1,
    limit = 10,
    search = "",
    category_id,
    brand_id,
    fields = [
      "_id",
      "product.brand",
      "product.brand_id",
      "product.category",
      "product.category_id",
      "product.is_taxable",
      "product.shipping_config",
      "product.production_status",
      "product.variants",
    ],
    sort = { field: "created_at", order: "desc" },
  } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/accessories/brands/${encodeURIComponent(brand_id)}/categories/${encodeURIComponent(category_id)}/products`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields,
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory products by brand/category (${response.status})`);
  }

  const data = await response.json();
  // API may return { data: [...], limit, page, total } or { results: [...], total }
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total, pagination: data?.pagination };
}

/** Accessory products by category (Add Accessory > Categories > Category). */
export async function getPostTypeAccessoryCategoryProducts(
  vendorId: string,
  postTypeId: string,
  token: string,
  params: AccessoryCategoryProductsParams,
  signal?: AbortSignal
): Promise<AccessoryBrandCategoryProductsResponse> {
  const {
    page = 1,
    limit = 10,
    search = "",
    category_id,
    fields = [
      "_id",
      "product.brand",
      "product.brand_id",
      "product.category",
      "product.category_id",
      "product.is_taxable",
      "product.shipping_config",
      "product.production_status",
      "product.variants",
    ],
    sort = { field: "created_at", order: "desc" },
  } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/accessories/categories/${encodeURIComponent(category_id)}/products`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields,
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory products by category (${response.status})`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total, pagination: data?.pagination };
}

export async function getPostTypeSlots(
  vendorId: string,
  postTypeId: string,
  token: string
): Promise<PostTypeSlotsResponse> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/slots`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch post type slots (${response.status})`);
  }

  return response.json();
}

/** Backend expects arrays of objects with id, name, logo/sku where applicable. */
export interface AccessoryRuleItemBrand {
  id: string;
  name: string;
  logo?: string;
}
export interface AccessoryRuleItemGroup {
  id: string;
  name: string;
}
export interface AccessoryRuleItemProduct {
  id: string;
  name: string;
  sku?: string;
}
export interface AccessoryRuleItemCategory {
  id: string;
  name: string;
  logo?: string;
}
export interface AccessoryRuleItemOffice {
  id: string;
  name: string;
}
export interface AccessoryRuleItemAgent {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface AccessoryRulePayload {
  brands: AccessoryRuleItemBrand[];
  categories: AccessoryRuleItemCategory[];
  products: AccessoryRuleItemProduct[];
  groups: AccessoryRuleItemGroup[];
  brokerage_firms: AccessoryRuleItemGroup[];
  offices: AccessoryRuleItemOffice[];
  agents: AccessoryRuleItemAgent[];
  status: boolean;
  select_all_brands?: boolean;
  select_all_categories?: boolean;
  select_all_groups?: boolean;
  select_all_brokerages?: boolean;
}

/** GET response may use old *_ids or new object arrays (brands, categories, etc). */
export interface AccessoryRule {
  _id?: string;
  id?: string;
  brand_ids?: string[];
  category_ids?: string[];
  product_ids?: string[];
  group_ids?: string[];
  brokerage_firm_ids?: string[];
  office_ids?: string[];
  agent_ids?: string[];
  brands?: AccessoryRuleItemBrand[];
  categories?: AccessoryRuleItemCategory[];
  products?: AccessoryRuleItemProduct[];
  groups?: AccessoryRuleItemGroup[];
  brokerage_firms?: AccessoryRuleItemGroup[];
  offices?: AccessoryRuleItemOffice[];
  agents?: AccessoryRuleItemAgent[];
  status?: boolean;
  /** New API: nested brand → categories → products (GET accessory-rules). */
  selections?: {
    brands?: Array<{
      brand?: { id?: string; name?: string; select?: boolean };
      select_all_categories?: boolean;
      categories?: Array<{
        category?: { id?: string; name?: string; select?: boolean };
        select_all_products?: boolean;
        products?: Array<{ id?: string; name?: string; sku?: string; select?: boolean }>;
      }>;
    }>;
    /** Top-level category rows (same rule may also list categories/products here for counts / quick picks). */
    categories?: Array<{
      category?: { id?: string; name?: string; select?: boolean };
      products?: Array<{ id?: string; name?: string; sku?: string; select?: boolean }>;
    }>;
    /** Top-level product picks */
    products?: Array<{ id?: string; name?: string; sku?: string; select?: boolean }>;
  };
  [key: string]: unknown;
}

/** Derive Browse Catalog counts + brand/category ids from accessory-rule GET (nested `selections.brands` or legacy flat arrays). */
export function summarizeAccessoryRuleCatalog(rule: unknown): {
  brand_ids: string[];
  category_ids: string[];
  total_brands: number;
  total_categories: number;
  total_products: number;
} {
  const empty = {
    brand_ids: [] as string[],
    category_ids: [] as string[],
    total_brands: 0,
    total_categories: 0,
    total_products: 0,
  };
  if (!rule || typeof rule !== "object") return empty;
  const r = rule as AccessoryRule & { selections?: AccessoryRule["selections"] };
  const selections = r.selections;
  if (selections && Array.isArray(selections.brands)) {
    const brand_ids: string[] = [];
    const categoryIdSet = new Set<string>();
    let nestedProductCount = 0;
    for (const row of selections.brands) {
      const bid = String(row?.brand?.id ?? "").trim();
      if (bid) brand_ids.push(bid);
      for (const catRow of row?.categories ?? []) {
        const cid = String(catRow?.category?.id ?? "").trim();
        if (cid) categoryIdSet.add(cid);
        const prods = catRow?.products ?? [];
        nestedProductCount += prods.length;
      }
    }
    for (const row of selections.categories ?? []) {
      const cid = String(row?.category?.id ?? "").trim();
      if (cid) categoryIdSet.add(cid);
    }
    const topCategories = selections.categories;
    const topProducts = selections.products;
    const total_brands = selections.brands.length;
    const total_categories = Array.isArray(topCategories) ? topCategories.length : categoryIdSet.size;
    const total_products = Array.isArray(topProducts) ? topProducts.length : nestedProductCount;
    return {
      brand_ids,
      category_ids: Array.from(categoryIdSet),
      total_brands,
      total_categories,
      total_products,
    };
  }
  const brands = r.brands ?? [];
  const categories = r.categories ?? [];
  const products = r.products ?? [];
  return {
    brand_ids: brands.map((b) => String((b as { id?: string }).id ?? "").trim()).filter(Boolean),
    category_ids: categories.map((c) => String((c as { id?: string }).id ?? "").trim()).filter(Boolean),
    total_brands: brands.length,
    total_categories: categories.length,
    total_products: Array.isArray(products) ? products.length : 0,
  };
}

export interface PutAccessoryRulesResponse {
  updated: boolean;
  total_rules: number;
}

/** Request body for PUT accessory-rules/{accessoryRuleId}/post-type. */
export interface PutAccessoryRulePostTypeBody {
  accessory_rule: {
    status: boolean;
    selections: {
      select_all_brands?: boolean;
      brands: Array<{
        brand: { id: string; name: string; logo?: string; select?: boolean };
        select_all_categories?: boolean;
        categories: Array<{
          category: { id: string; name: string; image?: string; select?: boolean };
          select_all_products?: boolean;
          products: Array<{ id: string; name: string; sku?: string; select?: boolean }>;
        }>;
      }>;
    };
    groups?: Array<{ groups: { id: string; name: string; image?: string; select?: boolean } }>;
    select_all_groups?: boolean;
    select_all_brokerage?: boolean;
    brokerage_firms?: Array<{ brokerages: { id: string; name: string; image?: string; select?: boolean } }>;
  };
  post_type: {
    name: string;
    slug: string;
    description: string;
    illustration: string;
    available_for_installation: boolean;
    is_default_post_type: boolean;
    status: boolean;
    slots: Array<{
      _id?: string;
      key: string;
      label: string;
      required: boolean;
      sort: number;
      zone: { x: number; y: number; w: number; h: number };
      input_type: string;
      status: boolean;
      selections: {
        brands: Array<{
          brand: { id: string; name: string; logo?: string };
          categories: Array<{
            category: { id: string; name: string; image?: string };
            products: Array<{ id: string; name: string; sku?: string }>;
          }>;
        }>;
        categories: unknown[];
        products: unknown[];
      };
    }>;
  };
}

/** Response for PUT accessory-rules/{accessoryRuleId}/post-type. */
export interface PutAccessoryRulePostTypeResponse {
  success: boolean;
  data: Record<string, unknown>;
}

export interface PatchAccessoryRuleResponse {
  updated: boolean;
}

/** Get accessory rules for a post type. */
export async function getAccessoryRules(
  vendorId: string,
  postTypeId: string,
  token: string,
  signal?: AbortSignal
): Promise<AccessoryRule[]> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/accessory-rules`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory rules (${response.status})`);
  }

  const data = await response.json();
  const rules = data?.data ?? data?.results ?? data?.rules ?? data;
  return Array.isArray(rules) ? rules : [];
}

/** Response shape for accessory-rules when called with user_id (Browse Catalog for end user). */
export interface GetAccessoryRulesForUserResponse {
  results: AccessoryRule[];
  total: number;
}

/** Get accessory rules for a post type filtered by end user (agent/teammember). Use for Browse Catalog in Accessory flow. */
export async function getAccessoryRulesForUser(
  vendorId: string,
  postTypeId: string,
  userId: string,
  token: string,
  signal?: AbortSignal
): Promise<GetAccessoryRulesForUserResponse> {
  const params = new URLSearchParams({ user_id: userId });
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/accessory-rules?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory rules for user (${response.status})`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results) ? data.results : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total };
}

/** Update accessory rule and linked post type (PUT accessory-rules/{accessoryRuleId}/post-type). */
export async function putAccessoryRulePostType(
  vendorId: string,
  postTypeId: string,
  accessoryRuleId: string,
  token: string,
  body: PutAccessoryRulePostTypeBody,
  signal?: AbortSignal
): Promise<PutAccessoryRulePostTypeResponse> {
  // const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/accessory-rules/${accessoryRuleId}/post-type`;
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to update accessory rule and post type (${response.status})`);
  }

  return response.json();
}

/** Update a single accessory rule (PATCH). */
export async function patchAccessoryRule(
  vendorId: string,
  postTypeId: string,
  ruleId: string,
  token: string,
  payload: Partial<Pick<AccessoryRulePayload, "status">>,
  signal?: AbortSignal
): Promise<PatchAccessoryRuleResponse> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/accessory-rules/${ruleId}`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to update accessory rule (${response.status})`);
  }

  return response.json();
}

export async function updatePostTypeStatus(
  vendorId: string,
  postTypeId: string,
  status: boolean,
  token: string
): Promise<{ success: boolean }> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}/status`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    const message = (errData as any)?.error ?? (errData as any)?.message ?? response.statusText;
    throw new Error(message);
  }

  return response.json().catch(() => ({ success: true }));
}


export async function deletePostType(
  vendorId: string,
  postTypeId: string,
  token: string
): Promise<void> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/${postTypeId}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    const message = (errData as any)?.error ?? (errData as any)?.message ?? response.statusText;
    throw new Error(message);
  }
}

/* ---------- It's me Srinivas Thimmisetty ---------- */

/* ---------- Select2 (lightweight dropdown) ---------- */

export interface PostTypeSelect2Item {
  id: string;
  text: string;
    /** Optional SVG/image URL to render alongside the post type name in dropdowns */
    post_url?: string;
}

export interface PostTypeSelect2Response {
  pagination: { more: boolean };
  results: PostTypeSelect2Item[];
}

export interface PostTypeSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
  sort?: { field: string; order: "asc" | "desc" };
  /** End customer — included in datatable body when set with license_type (scoped post types). */
  user_id?: string;
  license_type?: string;
}

function postTypeSelect2OrderPayload(params: PostTypeSelect2Params): Record<string, string> | undefined {
  const orderUserId = params.user_id?.trim();
  const orderLicenseType = params.license_type?.trim();
  if (!orderUserId || !orderLicenseType) return undefined;
  return { user_id: orderUserId, license_type: orderLicenseType };
}

export async function postTypesSelect2(
  vendorId: string,
  params: PostTypeSelect2Params,
  token: string,
  signal?: AbortSignal
): Promise<PostTypeSelect2Response> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/post-types/datatable`;
  const orderExtra = postTypeSelect2OrderPayload(params);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      search: params.search ?? "",
      sort: params.sort ?? { field: "name", order: "asc" },
      select2: true,
      ...orderExtra,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Post types select2 failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    pagination: data.pagination ?? { more: false },
    results: Array.isArray(data.results) ? data.results : [],
  };
}

/** POST vendors/{vendorId}/customer_side/datatable with select2: true — for post type dropdown options (id, text) */
export async function vendorCustomerSideSelect2(
  vendorId: string,
  params: PostTypeSelect2Params,
  token: string,
  signal?: AbortSignal
): Promise<PostTypeSelect2Response> {
  const url = `${getBaseUrl()}/vendors/${vendorId}/customer_side/datatable`;
  const orderExtra = postTypeSelect2OrderPayload(params);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      search: params.search ?? "",
      sort: params.sort ?? { field: "name", order: "asc" },
      select2: true,
      ...orderExtra,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Vendor customer_side select2 failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    pagination: data.pagination ?? { more: false },
    results: Array.isArray(data.results) ? data.results : [],
  };
}

/** Brand item from post-type-slots select2 response */
export interface SlotSelect2Brand {
  _id: string;
  name: string;
  text?: string;
  product_count?: number;
  categories?: string[];
  image?: string;
  [key: string]: unknown;
}

/** Category item from post-type-slots select2 response */
export interface SlotSelect2Category {
  _id: string;
  name: string;
  text?: string;
  product_count?: number;
  [key: string]: unknown;
}

/** Product item from post-type-slots select2 response */
export interface SlotSelect2Product {
  id: string;
  name: string;
  sku?: string;
}

/** Single slot result from GET .../post-type-slots/{slotId}/select2 */
export interface PostTypeSlotSelect2ResultItem {
  id?: string;
  slot_id?: string;
  key?: string;
  label?: string;
  post_type_id?: string;
  brands: SlotSelect2Brand[];
  categories: SlotSelect2Category[];
  products: SlotSelect2Product[];
  [key: string]: unknown;
}

export interface PostTypeSlotSelect2Response {
  pagination: { more: boolean };
  results: PostTypeSlotSelect2ResultItem[];
}

/** Params for select2-style pagination (load more on scroll) */
export interface PostTypeSlotSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
  /** When set, request a page of brands or categories only; backend may return paginated list */
  type?: "brands" | "categories";
}

/** Brand item from GET .../post-type-slots/{slotId}/brands/select2 */
export interface PostTypeSlotBrandSelect2Item {
  id: string;
  logo?: string;
  name: string;
  product_count?: number;
  select_all_categories?: boolean;
  text?: string;
}

export interface PostTypeSlotBrandsSelect2Response {
  pagination: { more: boolean };
  results: PostTypeSlotBrandSelect2Item[];
}

export interface PostTypeSlotBrandsSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/slots/{slotId}/brands/select2 — slot-linked brands (Browse Catalog > Brands) */
export async function getPostTypeSlotBrandsSelect2(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  token: string,
  params: PostTypeSlotBrandsSelect2Params = {}
): Promise<PostTypeSlotBrandsSelect2Response> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/brands/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot brands select2 (${response.status})`);
  }

  return response.json();
}

/** Row from GET vendors/{vendorId}/post-types/{postTypeId}/accessories/brands/select2 (Add Accessory → Browse Catalog → Brands). */
export interface PostTypeAccessoriesBrandSelect2Item {
  id?: string;
  _id?: string;
  name?: string;
  text?: string;
  logo?: string;
  product_count?: number;
  categories?: string[];
  category_count?: number;
  image?: string;
  [key: string]: unknown;
}

export interface PostTypeAccessoriesBrandsSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/accessories/brands/select2 — accessory Browse Catalog brands (no slot). */
export async function getPostTypeAccessoriesBrandsSelect2(
  vendorId: string,
  postTypeId: string,
  token: string,
  params: PostTypeAccessoriesBrandsSelect2Params = {},
  signal?: AbortSignal
): Promise<{ pagination: { more: boolean }; results: PostTypeAccessoriesBrandSelect2Item[] }> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/accessories/brands/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch accessory brands select2 (${response.status})`);
  }
  const json = await response.json();
  return {
    pagination: json?.pagination ?? { more: false },
    results: Array.isArray(json?.results)
      ? json.results
      : Array.isArray(json?.data)
        ? json.data
        : [],
  };
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/accessories/brands/{brandId}/categories/select2 — categories under a brand (Add Accessory). */
export async function getPostTypeAccessoriesBrandCategoriesSelect2(
  vendorId: string,
  postTypeId: string,
  brandId: string,
  token: string,
  params: FetchMarketplaceCategoriesByBrandParams = {},
  signal?: AbortSignal
): Promise<FetchMarketplaceCategoriesByBrandResult> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/accessories/brands/${encodeURIComponent(brandId)}/categories/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "");
    throw new Error(err || `Failed to fetch accessory brand categories select2 (${response.status})`);
  }
  const json = await response.json();
  const arr = json?.data ?? json?.results ?? json;
  const data = Array.isArray(arr)
    ? arr.map((c: { id?: string; text?: string; count?: number }) => ({
        id: String(c?.id ?? ""),
        text: String(c?.text ?? ""),
        count: c?.count,
      })).filter((c: { id: string }) => c.id)
    : [];
  const pagination = json?.pagination;
  const hasMore = Boolean(pagination?.more ?? (data.length >= limit));
  const total_categories =
    typeof json?.total_categories === "number" ? json.total_categories : undefined;
  return { data, hasMore, total_categories };
}

/** Response item from GET .../slots/{slotId}/categories/select2 (Browse Catalog > Categories) */
export interface PostTypeSlotCategoriesSelect2Item {
  id: string;
  name?: string;
  text?: string;
  count?: number;
  image?: string;
  select_all_products?: boolean;
}

export interface PostTypeSlotCategoriesSelect2Response {
  pagination: { more: boolean };
  results: PostTypeSlotCategoriesSelect2Item[];
}

export interface PostTypeSlotCategoriesSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/slots/{slotId}/categories/select2 — slot categories (Browse Catalog > Categories) */
export async function getPostTypeSlotCategoriesSelect2(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  token: string,
  params: PostTypeSlotCategoriesSelect2Params = {}
): Promise<PostTypeSlotCategoriesSelect2Response> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/categories/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot categories select2 (${response.status})`);
  }

  return response.json();
}

/** Category item from GET .../post-type-slots/{slotId}/brands/{brandId}/categories/select2 */
export interface PostTypeSlotBrandCategorySelect2Item {
  id: string;
  count?: number;
  image?: string;
  name: string;
  select_all_products?: boolean;
  text?: string;
}

export interface PostTypeSlotBrandCategoriesSelect2Response {
  pagination: { more: boolean };
  results: PostTypeSlotBrandCategorySelect2Item[];
}

export interface PostTypeSlotBrandCategoriesSelect2Params {
  page?: number;
  limit?: number;
  search?: string;
}

/** GET vendors/{vendorId}/post-types/{postTypeId}/slots/{slotId}/brands/{brandId}/categories/select2 — categories for a brand (Browse Catalog > Brand > Categories) */
export async function getPostTypeSlotBrandCategoriesSelect2(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  brandId: string,
  token: string,
  params: PostTypeSlotBrandCategoriesSelect2Params = {}
): Promise<PostTypeSlotBrandCategoriesSelect2Response> {
  const { page = 1, limit = 20, search = "" } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/brands/${encodeURIComponent(brandId)}/categories/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot brand categories select2 (${response.status})`);
  }

  return response.json();
}

export interface SlotBrandCategoryProductsDatatableParams {
  page?: number;
  limit?: number;
  search?: string;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
}

/** POST vendors/{vendorId}/post-types/{postTypeId}/slots/{slotId}/brands/{brandId}/categories/{categoryId}/products/datatable — Browse Catalog > Brand > Category > Products */
export async function getPostTypeSlotBrandCategoryProductsDatatable(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  brandId: string,
  categoryId: string,
  token: string,
  params: SlotBrandCategoryProductsDatatableParams = {},
  signal?: AbortSignal
): Promise<AccessoryBrandCategoryProductsResponse> {
  const {
    page = 1,
    limit = 10,
    search = "",
    fields = [
      "_id",
      "product.brand",
      "product.brand_id",
      "product.category",
      "product.category_id",
      "product.is_taxable",
      "product.shipping_config",
      "product.production_status",
      "product.variants",
    ],
    sort = { field: "created_at", order: "desc" },
  } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/brands/${encodeURIComponent(brandId)}/categories/${encodeURIComponent(categoryId)}/products/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields,
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot brand/category products datatable (${response.status})`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total, pagination: data?.pagination };
}

/** POST .../slots/{slotId}/categories/{categoryId}/products/datatable — Browse Catalog > Categories > category (no brand). */
export async function getPostTypeSlotCategoryProductsDatatable(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  categoryId: string,
  token: string,
  params: SlotBrandCategoryProductsDatatableParams = {},
  signal?: AbortSignal
): Promise<AccessoryBrandCategoryProductsResponse> {
  const {
    page = 1,
    limit = 10,
    search = "",
    fields = [
      "_id",
      "product.brand",
      "product.brand_id",
      "product.category",
      "product.category_id",
      "product.is_taxable",
      "product.shipping_config",
      "product.production_status",
      "product.variants",
    ],
    sort = { field: "created_at", order: "desc" },
  } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/categories/${encodeURIComponent(categoryId)}/products/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields,
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot category products datatable (${response.status})`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total, pagination: data?.pagination };
}

/** POST .../post-types/{postTypeId}/slots/{slotId}/products/datatable — Browse Catalog > Products */
export async function getPostTypeSlotAllProductsDatatable(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  token: string,
  params: SlotBrandCategoryProductsDatatableParams = {},
  signal?: AbortSignal
): Promise<AccessoryBrandCategoryProductsResponse> {
  const {
    page = 1,
    limit = 10,
    search = "",
    fields = [
      "_id",
      "product.brand",
      "product.brand_id",
      "product.category",
      "product.category_id",
      "product.is_taxable",
      "product.shipping_config",
      "product.production_status",
      "product.variants",
    ],
    sort = { field: "created_at", order: "desc" },
  } = params;
  const url = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/slots/${encodeURIComponent(slotId)}/products/datatable`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page,
      limit,
      search: search || "",
      fields,
      sort,
    }),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot all products datatable (${response.status})`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const total = typeof data?.total === "number" ? data.total : results.length;
  return { results, total, pagination: data?.pagination };
}

export async function getPostTypeSlotSelect2(
  vendorId: string,
  postTypeId: string,
  slotId: string,
  token: string,
  params: PostTypeSlotSelect2Params = {}
): Promise<PostTypeSlotSelect2Response> {
  const { page = 1, limit = 20, search = "", type } = params;
  const base = `${getBaseUrl()}/vendors/${encodeURIComponent(vendorId)}/post-types/${encodeURIComponent(postTypeId)}/post-type-slots/${encodeURIComponent(slotId)}/select2`;
  const url = new URL(base);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (search.trim()) url.searchParams.set("search", search.trim());
  if (type) url.searchParams.set("type", type);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to fetch slot select2 (${response.status})`);
  }

  return response.json();
}

export type SendLogOrderType = "orders" | "installation_orders" | "removal_orders";
export type SendLogType = "invoice" | "quote";

export interface CreateSendLogEntryParams {
  orderType: SendLogOrderType;
  orderId: string;
  type: SendLogType;
  token: string;
  signal?: AbortSignal;
}

/**
 * POST /send-log/:orderType/:orderId
 * body: { order_type: "orders" | "installation_orders" | "removal_orders", type: "invoice" | "quote" }
 */
export async function createSendLogEntry({
  orderType,
  orderId,
  type,
  token,
  signal,
}: CreateSendLogEntryParams): Promise<any> {
  const safeOrderId = String(orderId || "").trim();
  if (!safeOrderId) {
    throw new Error("orderId is required");
  }

  const response = await fetch(
    `${getBaseUrl()}/send-log/orders/${encodeURIComponent(safeOrderId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        order_type: orderType,
        type,
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || `Failed to create send log (${response.status})`);
  }

  return response.json().catch(() => ({}));
}
